// A single-select question as a stack of cards: a radiogroup whose rows
// are ChoiceCards, each with an optional caption beneath its label. Arrow
// keys move focus between rows; Enter or Space chooses, so a list that
// advances on choice never leaves the page under a keyboard user who is
// only looking. Choosing the `otherValue` row reveals a text field
// beneath it.

import { useRef, type KeyboardEvent } from "react";

import ChoiceCard from "components/ChoiceCard";
import {
  FieldError,
  FieldLabel,
  inputClass,
} from "components/FormV2/fields/chrome";

export interface ChoiceOption<Value extends string> {
  value: Value;
  label: string;
  caption?: string;
}

export interface OtherText {
  /** The option that reveals the field. */
  forValue: string;
  label: string;
  value: string;
  onChange: (text: string) => void;
  error?: string;
  maxLength?: number;
}

function arrowDelta(key: string): number {
  if (key === "ArrowDown" || key === "ArrowRight") return 1;
  if (key === "ArrowUp" || key === "ArrowLeft") return -1;
  return 0;
}

export default function ChoiceList<Value extends string>({
  label,
  options,
  value,
  onChoose,
  other,
}: {
  /** What the question is, for assistive technology. */
  label: string;
  options: readonly ChoiceOption<Value>[];
  value: Value | null;
  onChoose: (value: Value) => void;
  other?: OtherText;
}) {
  const rows = useRef<(HTMLDivElement | null)[]>([]);
  const focusable = options.findIndex((option) => option.value === value);
  const tabStop = focusable === -1 ? 0 : focusable;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta = arrowDelta(event.key);
    if (delta === 0) return;
    event.preventDefault();
    const current = rows.current.findIndex(
      (row) => row?.contains(document.activeElement) ?? false,
    );
    const next = (current + delta + options.length) % options.length;
    rows.current[next]?.querySelector("button")?.focus();
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        role="radiogroup"
        aria-label={label}
        className="flex flex-col gap-3"
        onKeyDown={onKeyDown}
      >
        {options.map((option, index) => (
          <div
            key={option.value}
            ref={(element) => {
              rows.current[index] = element;
            }}
          >
            <ChoiceCard
              asRadio
              tabIndex={index === tabStop ? 0 : -1}
              selected={value === option.value}
              onSelect={() => onChoose(option.value)}
              tone="daisy"
              size="lg"
            >
              <span className="block">{option.label}</span>
              {option.caption && (
                <span className="mt-1 block text-base font-normal text-oat-700">
                  {option.caption}
                </span>
              )}
            </ChoiceCard>
          </div>
        ))}
      </div>
      {other && value === other.forValue && (
        <div>
          <FieldLabel text={other.label} htmlFor="choice-other-text" />
          <input
            id="choice-other-text"
            type="text"
            value={other.value}
            maxLength={other.maxLength}
            className={inputClass(Boolean(other.error))}
            onChange={(event) => other.onChange(event.target.value)}
          />
          <FieldError message={other.error} />
        </div>
      )}
    </div>
  );
}
